import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PersonIcon from '@mui/icons-material/Person';
import SyncIcon from '@mui/icons-material/Sync';

// Storage key for sync preference (must match App.js)
const SYNC_PREFERENCE_KEY = 'syncResolutionPreference';

// Friendly names for sync preferences
const SYNC_PREFERENCE_LABELS = {
  cloud: 'Use cloud data',
  device: 'Use device data',
  merge: 'Merge both'
};

const TABS = [
  { id: 'account', label: 'Account', icon: PersonIcon },
  { id: 'sync', label: 'Sync', icon: SyncIcon },
];

const ProfileModal = ({ loggedInUser, onDeleteProfile }) => {
  const userEmail = typeof loggedInUser === 'string' ? loggedInUser : loggedInUser?.email;
  const userName = typeof loggedInUser === 'string' ? null : loggedInUser?.name;

  const [activeTab, setActiveTab] = useState('account');
  const [syncPreference, setSyncPreference] = useState(null);

  useEffect(() => {
    const savedPref = localStorage.getItem(SYNC_PREFERENCE_KEY);
    setSyncPreference(savedPref);
  }, []);

  const handleResetSyncPreference = () => {
    localStorage.removeItem(SYNC_PREFERENCE_KEY);
    setSyncPreference(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="profile-section">
            <h3 className="profile-section-title">Account</h3>
            <p className="profile-modal-email">
              {userName ? `${userName} (${userEmail})` : userEmail}
            </p>
            <div className="profile-modal-actions">
              <a
                href="https://billing.stripe.com/p/login/00g9Er4TpfdqdHOcMM"
                target="_blank"
                rel="noopener noreferrer"
                className="profile-modal-button profile-modal-button-blue"
              >
                Manage Subscription
              </a>
              <button
                onClick={onDeleteProfile}
                className="profile-modal-button profile-modal-button-red"
              >
                Delete Profile
              </button>
            </div>
          </div>
        );
      case 'sync':
        return (
          <div className="profile-section">
            <h3 className="profile-section-title">Sync Settings</h3>
            {syncPreference ? (
              <div className="profile-sync-preference">
                <span className="text-gray-400 text-sm">
                  Current: <strong>{SYNC_PREFERENCE_LABELS[syncPreference] || syncPreference}</strong>
                </span>
                <button
                  onClick={handleResetSyncPreference}
                  className="profile-modal-button-small"
                >
                  Reset
                </button>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                No preference saved. You'll be asked when conflicts occur.
              </p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="profile-modal-layout">
      <nav className="profile-sidebar">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`profile-sidebar-item ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon fontSize="small" />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="profile-content">
        {renderContent()}
      </div>
    </div>
  );
};

ProfileModal.propTypes = {
  loggedInUser: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      name: PropTypes.string
    })
  ]).isRequired,
  onDeleteProfile: PropTypes.func.isRequired,
};

export default ProfileModal;
